import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, NavigationStart, NavigationEnd, ParamMap, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import {
  DEFAULT_SUGGESTION_SORT,
  IMessagesFilters,
  ISuggestionsFilters,
  deserializeMessagesFilters,
  deserializeSuggestionsFilters,
  serializeMessagesFilters,
  serializeSuggestionsFilters,
} from 'src/app/filters';
import { AppComponent } from 'src/app/app.component';
import { IBase } from 'src/app/models';
import { CommunicationUserType, CommunicationType, SuggestionAffair, DriverSuggestionType, ObservationAffair, MessageStatus } from 'src/app/models/enums';
import { MessageReceiver } from 'src/app/models/enums/messageReceiver';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import { ISuggestion } from 'src/app/models/suggestion';
import { CommunicationsResolver } from 'src/app/resolvers/communications.resolver';
import { AuthService, BookingsService, MessageService, NotificationService, StorageService, TripsService } from 'src/app/services';
import { FavouritesService } from 'src/app/services/favourites/favourites.service';
import { ApiRouterService } from 'src/app/services/http/api.router';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialMessagesState } from 'src/app/store/initialisation/messages';
import { initialSuggestionState } from 'src/app/store/initialisation/suggestions';
import { images } from 'src/images';

@Component({
  selector: 'app-communications',
  templateUrl: './communications.component.html',
  styleUrls: ['./communications.component.scss'],
  standalone: false,
})
export class CommunicationsComponent implements OnInit, OnDestroy {

  public communicationType: CommunicationType;
  public communicationUserType: CommunicationUserType;

  public suggestionSelected: any;
  public newSuggestion: any;
  public editSuggestion: boolean = false;
  public shouldOpenConversation: boolean = false;
  public suggestions = initialSuggestionState;

  public messageSelected: any;
  public messageDetailSelected: any;
  public newMessage: any;
  public messages = initialMessagesState;

  public messageDetailsList: any = {};
  public messageDetailsPage = 1;
  
  public images = images;
  public CommunicationTypes = CommunicationType;
  public CommunicationUserType = CommunicationUserType;
  public SuggestionTypes = SuggestionType;
  public SuggestionStatus = SuggestionStatus;
  public SuggestionPriorities = SuggestionPriority;
  public TooltipPosition = TooltipPosition;

  private routerSubscription: Subscription;
  private queryParamsSub?: Subscription;
  private routeParamsSub?: Subscription;
  private interval: any;
  private destroyed = false;

  public searching = false;
  public searchValue: string = '';

  private paramsHandledNavigation: boolean = false;
  // Suggestions filters (driven by URL)
  private suggestionClientsSelected: number[] = [];
  private prioritiesSelected: SuggestionPriority[] = [];
  private townSelected: number[] = [];
  private servicesSelected: number[] = [];
  private typesSelected: SuggestionType[] = [];
  private statusSelected: SuggestionStatus[] = [];
  // Messages filters (driven by URL)
  private clientsSelected: IBase[] = [];
  private sendersSelected: IBase[] = [];
  private receiversSelected: IBase[] = [];

  public messageDetailModal: Modal;
  public messageDetailShowed: boolean = false;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    private suggestionService: SuggestionService,
    private communicationsResolver: CommunicationsResolver,
    private activatedRoute: ActivatedRoute,
    public utilsService: UtilsService,
    public routerService: ApiRouterService,
    public storageService: StorageService,
    public authService: AuthService,
    public router: Router,
    public notificationService: NotificationService,
    public tripsService: TripsService,
    public favouritesService: FavouritesService,
    public bookingsService: BookingsService,
    public messageService: MessageService,
    public appComponent: AppComponent,
  ) {
    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById(`${this.communicationType}DetailContainer`)!;
      const componentOpen = document.getElementById(`${this.communicationType}Container`)!;
      const modals: Node[] = [];
      modals.push(document.getElementById('messageDetail') as Node);
      modals.push(document.getElementById('exceedReceiversModal') as Node);
      modals.push(document.getElementById('additionalCostModal') as Node);
      modals.push(document.getElementById('conversationModal') as Node);
      const close = utilsService.closeCustomModalsWithModals(e, modal, modals, componentOpen);
      if (close && (this.suggestionSelected || this.messageSelected)) {
        this.router.navigate([`${this.communicationType}/${this.communicationUserType}`], {
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      }
    });
  }

  async ngOnInit() {
    this.literalService.setTitle('sidebar.communications', ' - NEMI Backoffice');

    this.communicationType = this.activatedRoute.routeConfig?.path?.split('/')[0] as CommunicationType;
    this.communicationUserType = this.activatedRoute.routeConfig?.path?.split('/')[1] as CommunicationUserType;

    this.getCommunicationsListener();

    // Path params drive: opening a suggestion/message detail by :id.
    this.routeParamsSub = this.activatedRoute.paramMap.subscribe((params) => {
      const commId = params.get('id');
      const openConversation = this.activatedRoute.snapshot.queryParamMap.get('openConversation') === 'true';
      if (commId) {
        this.paramsHandledNavigation = true;
        if (this.communicationType === CommunicationType.SUGGESTIONS) {
          this.showSuggestionData({id: Number(commId), openConversation});
        } else {
          this.showMessageData(Number(commId));
        }
      } else {
        if (this.suggestionSelected) this.suggestionSelected = undefined;
        if (this.messageSelected) this.messageSelected = undefined;
      }
    });

    // Hydration from storage happens in communicationsHydrationGuard before the
    // resolver fires, so there's exactly one API call per sidebar entry.

    // Query params drive: filters, page, search, sort, plus the "open from notification" flow.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.applyQueryParamsToState(params);
      this.handleNotificationReentry(params);
    });

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeModals();
      }
      if (event instanceof NavigationEnd) {
        this.communicationType = this.activatedRoute.routeConfig?.path?.split('/')[0] as CommunicationType;
        this.communicationUserType = this.activatedRoute.routeConfig?.path?.split('/')[1] as CommunicationUserType;
      }
    });
  }

  private applyQueryParamsToState(params: ParamMap): void {
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      const filters: ISuggestionsFilters = params.keys.length > 0
        ? deserializeSuggestionsFilters(params)
        : { selectedClients: [], selectedTowns: [], selectedServices: [], selectedTypes: [], selectedStatus: [], selectedPriorities: [] };
      this.suggestionClientsSelected = filters.selectedClients;
      this.townSelected = filters.selectedTowns;
      this.servicesSelected = filters.selectedServices.map(s => s.id!).filter(id => id != null);
      this.typesSelected = filters.selectedTypes;
      this.statusSelected = filters.selectedStatus;
      this.prioritiesSelected = filters.selectedPriorities;
    } else {
      const filters: IMessagesFilters = params.keys.length > 0
        ? deserializeMessagesFilters(params)
        : { selectedClients: [], selectedSenders: [], selectedReceivers: [] };
      this.clientsSelected = filters.selectedClients;
      this.sendersSelected = filters.selectedSenders;
      this.receiversSelected = filters.selectedReceivers;
    }

    const pageParam = params.get('page');
    const targetState = this.communicationType === CommunicationType.SUGGESTIONS ? this.suggestions : this.messages;
    if (pageParam) {
      const page = Number(pageParam);
      if (!isNaN(page) && page > 0) targetState.pagination.actualPage = page;
    } else {
      targetState.pagination.actualPage = 1;
    }

    this.searchValue = params.get('search') ?? '';
  }

  private handleNotificationReentry(params: ParamMap): void {
    // Skip if the path-params subscription already handled this navigation in the same tick.
    if (this.paramsHandledNavigation) {
      this.paramsHandledNavigation = false;
      return;
    }
    if (!params.get('t')) return;
    const commId = Number(this.activatedRoute.snapshot.params['id']);
    if (!commId) return;
    const openConversation = params.get('openConversation') === 'true';
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      if (this.suggestionSelected?.id !== commId || openConversation) {
        this.showSuggestionData({id: commId, openConversation});
      }
    } else if (this.messageSelected?.id !== commId) {
      this.showMessageData(commId);
    }
  }

  closeModals() {
    if (this.messageDetailShowed) this.messageDetailModal.toggle();
  }

  getCommunicationsListener() {
    this.interval = setInterval(() => {
      this.refreshData();
    }, 60000);
  }

  // In-place refresh from the resolver (used by the 60s interval). Resolver reads URL.
  async refreshData() {
    this.activatedRoute.snapshot.data['suggestions'] = this.suggestions;
    this.activatedRoute.snapshot.data['messages'] = this.messages;
    this.activatedRoute.snapshot.data['type'] = this.communicationType;
    this.activatedRoute.snapshot.data['userType'] = this.communicationUserType;
    this.searching = true;
    await this.communicationsResolver.getCommunications(
      this.activatedRoute.snapshot,
      undefined,
      true,
    ).finally(() => {
      this.searching = false;
    });
  }

  async showSuggestionData(data: any) {
    try {
      // Reset first to ensure change detection works when opening conversation again
      this.shouldOpenConversation = false;

      this.suggestionSelected = (await this.suggestionService.getSuggestionById(data.id)) as ISuggestion;
      this.newSuggestion = undefined;
      this.editSuggestion = data.edit ?? false;

      if (data.openConversation) {
        setTimeout(() => {
          this.shouldOpenConversation = true;
        }, 0);
      }

      // Navigate only if the URL doesn't already point at this detail; the paramMap
      // subscription will reenter once the URL matches. Preserves query params.
      const currentId = this.activatedRoute.snapshot.paramMap.get('id');
      if (currentId !== String(data.id)) {
        this.router.navigate([`${this.communicationType}/${this.communicationUserType}/${data.id}`], {
          queryParams: this.activatedRoute.snapshot.queryParams,
        });
      }
    } catch {
      this.router.navigate([`${this.communicationType}/${this.communicationUserType}`]);
    }
  }

  changePage(event: any) {
    const { isNext, specificPage } = event;
    const targetState = this.communicationType === CommunicationType.SUGGESTIONS ? this.suggestions : this.messages;
    const current = targetState.pagination.actualPage || 1;
    const newPage = specificPage ?? (isNext ? current + 1 : current - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage > 1 ? newPage : null },
      queryParamsHandling: 'merge',
    });
  }

  closeDetails() {
    if (this.communicationType === this.CommunicationTypes.SUGGESTIONS) {
      this.newSuggestion = undefined;
      this.editSuggestion = false;
      this.shouldOpenConversation = false;
    } else {
      this.newMessage = undefined;
    }
    this.router.navigate([`${this.communicationType}/${this.communicationUserType}`], {
      queryParams: this.activatedRoute.snapshot.queryParams,
    });
  }

  async addCommunication() {
    if (this.communicationType === this.CommunicationTypes.SUGGESTIONS) {
      this.newSuggestion = {
        type: this.communicationUserType === CommunicationUserType.USERS ? SuggestionType.COMPLAINT : DriverSuggestionType.OBSERVATION,
        affair: this.communicationUserType === CommunicationUserType.USERS ? SuggestionAffair.SERVICE_IMPROVEMENT : ObservationAffair.DRIVER_APP,
        description: '',
        comments: '',
        creation: undefined,
        lastModification: undefined,
        priority: SuggestionPriority.MEDIUM,
        status: SuggestionStatus.PENDING,
        town: undefined,
        client: undefined,
        service: undefined,
        user: undefined,
      };
    } else {
      this.newMessage = {
        client: null,
        receiver: this.communicationUserType === CommunicationUserType.USERS ? MessageReceiver.USER : MessageReceiver.DRIVER,
        receiverIds: [],
        subject: '',
        content: '',
        channels: [],
        receiverServiceId: undefined,
      }
    }
  }

  suggestionEdited(suggestionEdited: ISuggestion) {
    const index = this.suggestions.value.findIndex((suggestion) => suggestion.id === suggestionEdited.id);
    if (index !== -1) {
      this.suggestions.value[index] = suggestionEdited;
    } else {
      this.refreshData();
      this.newSuggestion = undefined;
    }
    if (this.suggestionSelected && this.suggestionSelected.id === suggestionEdited.id) this.suggestionSelected = suggestionEdited;
  }

  // initFilters removed: filter state is now driven by the queryParamMap subscription
  // (applyQueryParamsToState). Storage fallback happens in the resolver / hydration.

  filter(event: any) {
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      const filters: ISuggestionsFilters = {
        selectedClients: event.selectedClients ?? [],
        selectedTowns: event.selectedTowns ?? [],
        selectedServices: event.selectedServices ?? [],
        selectedTypes: event.selectedTypes ?? [],
        selectedStatus: event.selectedStatus ?? [],
        selectedPriorities: event.selectedPriorities ?? [],
      };
      this.storageService.setSuggestionsFilters(JSON.stringify(filters));
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...serializeSuggestionsFilters(filters), page: null },
        queryParamsHandling: 'merge',
      });
    } else {
      const filters: IMessagesFilters = {
        selectedClients: event.selectedClients ?? [],
        selectedSenders: event.selectedSenders ?? [],
        selectedReceivers: event.receivers?.selectedReceivers ?? [],
      };
      this.storageService.setMessagesFilters(JSON.stringify(filters));
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...serializeMessagesFilters(filters), page: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  search(data: any) {
    const value = typeof data === 'object' ? data.target.value : data;
    const currentSearch = this.activatedRoute.snapshot.queryParamMap.get('search') || null;
    if ((value || null) === currentSearch) {
      this.refreshData();
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: value || null, page: null },
      queryParamsHandling: 'merge',
    });
  }

  sortSuggestions() {
    this.storageService.getSuggestionsSortType().then((sort) => {
      const isDefault = !sort || sort === DEFAULT_SUGGESTION_SORT;
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { sort: isDefault ? null : sort },
        queryParamsHandling: 'merge',
      });
    });
  }

  changeCommunication(value: CommunicationType) {
    // Switching SUGGESTIONS <-> MESSAGES uses a different filter set; do not preserve queryParams.
    this.communicationType = value;
    this.router.navigate([`${this.communicationType}/${this.communicationUserType}`]);
  }

  toggleUserType() {
    const newUserType = this.communicationUserType === CommunicationUserType.USERS
      ? CommunicationUserType.DRIVERS
      : CommunicationUserType.USERS;
    this.communicationUserType = newUserType;
    // Filters are scoped to a user type; do not preserve queryParams across the switch.
    this.router.navigate([`${this.communicationType}/${this.communicationUserType}`]);
  }

  updatePriority(data: any) {
    const suggestionToUpdate = structuredClone(this.suggestions.value.find((suggestion) => suggestion.id === data.id));
    if (suggestionToUpdate !== undefined) {
      suggestionToUpdate.priority = data.value;
      this.suggestionService.updateSuggestion(suggestionToUpdate).then((response) => {
        this.suggestionEdited(response as ISuggestion);
      })
    }
  }

  updateStatus(data: any) {
    const suggestionToUpdate = structuredClone(this.suggestions.value.find((suggestion) => suggestion.id === data.id));
    if (suggestionToUpdate !== undefined) {
      suggestionToUpdate.status = data.value;
      this.suggestionService.updateSuggestion(suggestionToUpdate).then((response) => {
        this.suggestionEdited(response as ISuggestion);
      })
    }
  }

  getCommentsText() {
    if (this.communicationUserType === CommunicationUserType.USERS) return CommunicationUserType.DRIVERS
    return CommunicationUserType.USERS
  }

  showMessageData(id: number) {
    this.messageSelected = this.messages.value.find((message) => message.id === id);
    this.newMessage = undefined;
    const currentId = this.activatedRoute.snapshot.paramMap.get('id');
    if (currentId !== String(id)) {
      this.router.navigate([`${this.communicationType}/${this.communicationUserType}/${id}`], {
        queryParams: this.activatedRoute.snapshot.queryParams,
      });
    }
  }

  async showMessageDetail(id: number) {
    this.messageDetailModal = new Modal(document.getElementById('messageDetail'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.messageDetailShowed = false),
      onShow: () => (this.messageDetailShowed = true),
    });
    this.messageDetailSelected = {...this.messages.value.find((message) => message.id === id)};
    this.messageDetailsList = await this.getMessageDetail(id, this.messageDetailsPage, MessageStatus.FAILED);
    this.messageSelected = undefined;
    this.newMessage = undefined;
    this.messageDetailModal.toggle();
  }

  openUserDetail(id: number) {
    window.open(`/users${this.communicationUserType === CommunicationUserType.DRIVERS ? `/driver` : ''}/${id}`, '_blank');
  }

  async updateMessageDetail(event: any) {
    if (event.pagination) {
      if (event.pagination.specificPage) this.messageDetailsPage = event.pagination.specificPage;
      else if (event.pagination.isNext) this.messageDetailsPage += 1;
      else this.messageDetailsPage -= 1;
    }
    this.messageDetailsList = await this.getMessageDetail(this.messageDetailSelected.id, this.messageDetailsPage, event.status, event.searchValue);
  }

  async getMessageDetail(id: number, page: number, status: MessageStatus, searchValue?: string) {
    return await this.messageService.getMessageDetails(id, page, 10, status, searchValue);
  }

  abortFetchEventSource() {
    clearInterval(this.interval);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.interval);
    this.routerSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.routeParamsSub?.unsubscribe();
  }
}
